import {CURRENT_PAGE_NAME_TOKEN} from './constants';
import {resolveCurrentPageName} from './resolveCurrentPageName';
import {resolveOgImageSource} from './resolveOgImageSource';
import type {CurrentPage, OgImageValue} from './types';
import {singleMemoMulti} from '@packages/utilities/memo';

/**
 * Pre-resolved page fields that both Canvas and SSR provide.
 *
 * Canvas extracts these from Immutable PageRecordType + localization helpers.
 * SSR extracts these from normalizePageJson (which pre-applies ogTitleCopied,
 * locale resolution, etc.).
 */
export type ResolvedPageInputs = {
  pageName: string;
  parentFolderName: string | undefined;
  seoTitle: string | undefined;
  seoDesc: string | undefined;
  ogTitle: string | undefined;
  ogDesc: string | undefined;
  ogImageAssetId: string | null | undefined;
  ogImage: string | null | undefined;
};

/**
 * Environment-specific token resolver used by Designer and SSR.
 */
export type ReplaceCurrentPageTokens = (value: string) => string | null;

/**
 * Shape-agnostic core for resolving current-page environment values.
 *
 * Asset-bound OG images stay as `{id}` in both Designer and SSR page-binding
 * paths. The only environment-specific difference here is token replacement.
 */
function resolveTextField(
  value: string | undefined,
  replaceTokens: ReplaceCurrentPageTokens
): string | undefined {
  return value ? (replaceTokens(value) ?? value) : undefined;
}

/**
 * Returns a `page` value for the renderer evaluation environment
 *
 * Evaluates tokens using the given token replacement function that
 * should handle all mapping of tokens to the correct values.
 *
 * WARNING! Treat the return value as immutable and do not mutate it.
 * There is light memoization in place to ensure that consecutive calls
 * that result in the same value also return the same reference.
 */
export function resolveCurrentPage(
  inputs: ResolvedPageInputs,
  replaceTokens: ReplaceCurrentPageTokens
): CurrentPage {
  const resolvedCmsItemName = replaceTokens(CURRENT_PAGE_NAME_TOKEN);

  const ogImage = resolveOgImageSource(
    inputs.ogImageAssetId,
    inputs.ogImage,
    replaceTokens
  );

  return memo_buildCurrentPageValue(
    resolveCurrentPageName(resolvedCmsItemName, inputs.pageName),
    inputs.parentFolderName,
    resolveTextField(inputs.seoTitle, replaceTokens),
    resolveTextField(inputs.seoDesc, replaceTokens),
    resolveTextField(inputs.ogTitle, replaceTokens),
    resolveTextField(inputs.ogDesc, replaceTokens),
    ogImage
  );
}

/**
 * Helper for maintaining resolveCurrentPage return value referential equality.
 *
 * Objective is to ensure that the current page value returned by
 * resolveCurrentPage remains referentially as stable as possible across
 * calls if the output doesn't change.
 */
const memo_buildCurrentPageValue = singleMemoMulti(
  (
    name: string,
    parentFolderName: string | undefined,
    seoTitle: string | undefined,
    seoDesc: string | undefined,
    ogTitle: string | undefined,
    ogDesc: string | undefined,
    ogImage: OgImageValue
  ): CurrentPage => ({
    name,
    parentFolderName,
    seoTitle,
    seoDesc,
    ogTitle,
    ogDesc,
    ogImage,
  })
);
